import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';
import { useActiveTams } from '../../hooks/useActiveTams';
import { useCLevelAnnualSynthesis } from '../../hooks/useCLevelAnnualSynthesis';
import { useCLevelMarginRanking } from '../../hooks/useCLevelMarginRanking';
import type { CLevelMarginRankingMode, CLevelSynthesisViewMode } from '../../types/primes';
import { isYearConsultable } from '../../utils/clevelPrimesDashboard.utils';
import AnnualYearNavigator from '../molecules/AnnualYearNavigator';
import CLevelPrimesDashboardTable from '../molecules/CLevelPrimesDashboardTable';
import CLevelSynthesisKpisBand from '../molecules/CLevelSynthesisKpisBand';
import CLevelSynthesisViewTabs from '../molecules/CLevelSynthesisViewTabs';
import CLevelTamMarginRanking from '../molecules/CLevelTamMarginRanking';

const CLevelPrimesDashboard = () => {
  const { t } = useTranslation(['primes']);
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState(() => format(new Date(), 'yyyy'));
  const [synthesisViewMode, setSynthesisViewMode] = useState<CLevelSynthesisViewMode>('marge');
  const [marginRankingMode, setMarginRankingMode] = useState<CLevelMarginRankingMode>('annual');

  const { activeTams, loading, error } = useActiveTams({
    fetchPolicy: 'cache-and-network',
    onError: (queryError) => {
      errorLogger(queryError, { extraInfos: 'getActiveTams failed on /primes/dashboard' });
    },
  });
  const synthesisRows = useCLevelAnnualSynthesis(activeTams, selectedYear);
  const {
    rows: marginRankingRows,
    loading: marginRankingLoading,
    lifetimeError,
    lifetimeDisplayAmountByTamEmail,
    lifetimeLoading,
    cumuls: lifetimeMarginCumuls,
  } = useCLevelMarginRanking(activeTams, synthesisRows, marginRankingMode);
  const yearConsultable = isYearConsultable(selectedYear);
  const tableLoading = loading || (yearConsultable && synthesisRows.some((row) => row.rowStatus === 'loading'));
  const loggedLifetimeErrorRef = useRef(false);

  useEffect(() => {
    if (!lifetimeError) {
      loggedLifetimeErrorRef.current = false;
      return;
    }
    if (loggedLifetimeErrorRef.current) {
      return;
    }
    loggedLifetimeErrorRef.current = true;
    errorLogger(lifetimeError, {
      extraInfos: 'getLifetimeMarginCumuls failed on /primes/dashboard',
    });
  }, [lifetimeError]);

  useEffect(() => {
    const state = location.state as { primesDashboardToastKey?: string } | null;
    const toastKey = state?.primesDashboardToastKey;
    if (!toastKey) {
      return;
    }
    notify('warning', t(toastKey), undefined, {
      hideProgressBar: true,
      toastId: 'clevel-detail-redirect',
    });
    navigate('.', { replace: true, state: {} });
  }, [location.state, navigate, t]);

  if (error) {
    return <Message variant="body1">{t('loadingTamsError')}</Message>;
  }

  if (!loading && activeTams.length === 0) {
    return <Message variant="body1">{t('emptyTams')}</Message>;
  }

  return (
    <Container>
      <CLevelSynthesisKpisBand
        activeTams={activeTams}
        synthesisRows={synthesisRows}
        selectedYear={selectedYear}
        lifetimeMarginCumuls={lifetimeMarginCumuls}
        lifetimeLoading={lifetimeLoading}
        lifetimeMarginCumulsFailed={lifetimeError !== undefined}
      />

      <CLevelTamMarginRanking
        rows={marginRankingRows}
        mode={marginRankingMode}
        selectedYear={selectedYear}
        onModeChange={setMarginRankingMode}
        loading={loading || marginRankingLoading}
        loadErrorMessage={lifetimeError ? t('clevelMarginRankingLoadError') : undefined}
      />

      <ToolbarRow>
        <CLevelSynthesisViewTabs value={synthesisViewMode} onChange={setSynthesisViewMode} />
        <YearNavigatorWrapper>
          <AnnualYearNavigator selectedYear={selectedYear} onSelectedYearChange={setSelectedYear} />
        </YearNavigatorWrapper>
      </ToolbarRow>

      <CLevelPrimesDashboardTable
        rows={synthesisRows}
        loading={tableLoading}
        selectedYear={selectedYear}
        viewMode={synthesisViewMode}
        lifetimeDisplayAmountByTamEmail={lifetimeDisplayAmountByTamEmail}
        lifetimeMarginLoading={lifetimeLoading}
      />
    </Container>
  );
};

export default CLevelPrimesDashboard;

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(3),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

const ToolbarRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const YearNavigatorWrapper = styled('div')(() => ({
  marginLeft: 'auto',
}));

const Message = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
}));
